import { Card, CardBody, CardHeader, Divider } from "@heroui/react";
import { DateValue } from "@internationalized/date";
import i18nInstance from "@/i18n";

/**
 * Type representing a new value to be created
 */
type NewValue = {
  updated: any;
};

/**
 * Formats a value for display, handling different data types
 *
 * @param value - The value to format
 * @returns Formatted string representation of the value
 */
const formatValue = (value: string | DateValue | undefined) => {
  if (value === undefined || value === null) return "";

  if (typeof value === "object" && "toLocaleString" in value) {
    return value.toLocaleString();
  }

  return String(value);
};

/**
 * Component that displays new values to be created
 *
 * Unlike the Diff component which shows both original and updated values,
 * this component only shows the new values in green, as there are no original
 * values when creating a new item.
 *
 * @param newValues - Record of field names and their new values
 * @param exclude_key - Array of keys to exclude from display
 * @param translations - Optional record for translating field names
 * @returns JSX element displaying the new values
 */
function NewDiff(
  newValues: Record<string, NewValue>,
  exclude_key: ReadonlyArray<string> = [],
  translations?: Record<string, string>,
  tableName?: string,
) {
  const resolveLabel = (key: string) => {
    if (tableName) {
      const fieldsKey = `${tableName}.fields.${key}`;
      const fieldsLabel = i18nInstance.t(fieldsKey, {
        ns: "tables",
        defaultValue: fieldsKey,
      });
      if (fieldsLabel !== fieldsKey) return fieldsLabel;

      const columnsKey = `${tableName}.columns.${key}`;
      const columnsLabel = i18nInstance.t(columnsKey, {
        ns: "tables",
        defaultValue: columnsKey,
      });
      if (columnsLabel !== columnsKey) return columnsLabel;
    }

    return translations?.[key] || key;
  };

  return Object.entries(newValues)
    .filter(([key]) => !exclude_key.includes(key))
    .map(([key, { updated }]) => (
      <Card key={key} className="mb-4 min-h-min">
        <CardHeader className="text-lg font-semibold text-gray-800">
          {resolveLabel(key)}
        </CardHeader>
        <Divider />
        <CardBody className="text-lg font-semibold bg-slate-100">
          <div className="text-green-600">
            <span className="font-mono text-sm">
              + {formatValue(updated) ?? "N/A"}
            </span>
          </div>
        </CardBody>
      </Card>
    ));
}

export { NewDiff, type NewValue };
